<template lang="pug">
  v-card.main-wrapper
    v-snackbar(
      :value="isSaving && fileSavingPercentage > 0"
      color="success"
      :timeout="0"
    ) File uploading progress {{ fileSavingPercentage }}%
    div.d-flex.pl-6.pr-3.align-center.card-header(v-if="_title")
      h3 {{ _title }}
    v-divider(v-if="_title")
    div.body
      v-form(ref="form" v-if="model").px-6.pt-4
        v-row
          v-col.license-types.py-0(cols="12")
            div.license-types__label License type
            div.license-types__row
              v-checkbox.mr-4(
                v-model="model.is_auto"
                label="Auto"
                :error="formErrors.hasError('licenseTypes')"
                @change="formErrors.validField('licenseTypes')"
              )
              v-checkbox.mr-4(
                v-model="model.is_motor"
                label="Motor"
                :error="formErrors.hasError('licenseTypes')"
                @change="formErrors.validField('licenseTypes')"
              )
              v-checkbox(
                v-model="model.is_scooter"
                label="Scooter"
                :error="formErrors.hasError('licenseTypes')"
                @change="formErrors.validField('licenseTypes')"
              )
            div.license-types__error(v-if="formErrors.hasError('licenseTypes')")
              | {{ formErrors.fields.licenseTypes || 'Please select at least one license type' }}
        v-row
          v-col(cols="6")
            v-autocomplete(
              :rules="[RULES.REQUIRED]"
              label='Category'
              placeholder="Choose Category"
              :error="formErrors.hasError('category')"
              :error-messages="formErrors.fields.category"
              v-model="model.categoryId"
              :items="$store.state.trainingCategories.items"
              item-value="id"
              item-text="name"
              :menu-props="{ closeOnClick: true }"
              dense
            )
          v-col(cols="6")
            v-autocomplete(
              label='Layout'
              placeholder="Choose Layout"
              :error="formErrors.hasError('layout')"
              :error-messages="formErrors.fields.layout"
              v-model="model.layout"
              :items="LAYOUT_OPTIONS"
              item-value="query"
              item-text="name"
              :menu-props="{ closeOnClick: true }"
              @change="onLayoutChange(model.layout)"
              dense
            )
          v-col(
            v-if="model.layout === LAYOUT_OPTIONS_QUERY.QUESTIONS || model.layout === LAYOUT_OPTIONS_QUERY.GEVAARHERKENNING"
            cols="6"
          )
            v-autocomplete(
              label='Type'
              placeholder="Choose Type"
              :error="formErrors.hasError('layout')"
              :error-messages="formErrors.fields.layout"
              v-model="model.type"
              :items="TYPE_OPTIONS"
              item-value="query"
              :menu-props="{ closeOnClick: true }"
              item-text="name"
              dense
              clearable
            )
          v-col(cols='12')
            v-text-field(
              label='Short name'
              v-model="model.name"
              :error="formErrors.hasError('name')"
              :error-messages="formErrors.fields.name"
              :rules="[RULES.REQUIRED]"
              dense
            )
          v-col(
            cols='12'
            v-if="model.layout !== LAYOUT_OPTIONS_QUERY.THEORIE"
          )
            v-text-field(
              label='Question'
              v-model="model.content"
              :error="formErrors.hasError('content')"
              :error-messages="formErrors.fields.content"
              dense
            )
          v-col(
            cols='12'
            v-if="isTheorieLayout"
          )
            h4 Question
            div.summernote-content
              vue-summernote(
                ref="editor"
                :placeholder="editorPlaceholder"
                @onChange="editorChange"
                @onImageUpload="editorImageUpload"
              ).summernote-editor
            image-crop(
              v-model="cropModalVisible"
              :image="cropImage"
              :cropperOptions="cropperOptions"
              :processing="videoProcessing"
              @onCrop="saveToServer"
            )
          v-col(
            cols="12"
            v-if="isVideoLayout"
          )
            h4.mb-3 Video
              nu-player(
                v-if="(video && video.status === 'ACTIVE') || (model.videos && model.videos.length && !removeVideo && !(model.video_meta && model.video_meta.status === 'PROCESSING'))"
                :video-files="model.videos"
                :video-file="video"
                :poster="model.thumbnail"
                :video-meta="model.video_meta"
                :subtitles="subtitlesLinkNl"
                :subtitles-en="subtitlesLinkEn"
              )
            div.d-flex.align-center.mb-3(
              v-if="((model.video_meta && (model.video_meta.subtitles_file_nl || model.video_meta.subtitles_file_en)) || videoFile) && !removeVideo"
            )
              span {{ videoFile && videoFile.name ? videoFile.name : '' }}
              v-btn(icon @click="resetVideoUpload" v-if="videoFile")
                v-icon mdi-close
            video-encoding-status(v-if="video && video.encoding_job && video.status !== 'ACTIVE'"
              :encoding-job="video.encoding_job")
            div.d-flex(
              v-if="!videoFile && (!model.video || video.status !== 'PROCESSING')"
            )
              video-uploader(
                @on-file-load="onSelectVideo"
                v-slot="{fileDragOver}"
              )
                v-btn(outlined :color="fileDragOver ? 'primary' : ''")
                  v-icon.mr-2(v-if="!fileDragOver") mdi-cloud-upload-outline
                  v-icon.mr-2(v-else) mdi-cloud-upload
                  span Upload video
            h4.mb-3 Subtitles NL
            h5.mb-3(
              v-if="((model.video_meta && model.video_meta.subtitles_file_nl) || subtitlesFileNl) && !removeSubtitlesNl"
            ) {{ subtitlesFileNl && subtitlesFileNl.name ? subtitlesFileNl.name : model.video_meta.subtitles_file_nl }}
            div.d-flex
              subtitles-uploader(
                @on-file-load="onSelectSubtitlesNl"
                v-slot="{fileDragOver}"
              )
                v-btn(outlined :color="fileDragOver ? 'primary' : ''")
                  v-icon.mr-2(v-if="!fileDragOver") mdi-cloud-upload-outline
                  v-icon.mr-2(v-else) mdi-cloud-upload
                  span Upload file
              v-btn(outlined color="primary" v-if="model.video_meta && model.video_meta.subtitles_file_nl && !removeSubtitlesNl" @click="onRemoveCurrentSubtitlesNl").ml-4
                v-icon.mr-2 mdi-close
                span Remove current subtitles
            h4.mb-3.mt-6 Subtitles EN
            h5.mb-3(
              v-if="((model.video_meta && model.video_meta.subtitles_file_en) || subtitlesFileEn) && !removeSubtitlesEn"
            ) {{ subtitlesFileEn && subtitlesFileEn.name ? subtitlesFileEn.name : model.video_meta.subtitles_file_en }}
            div.d-flex
              subtitles-uploader(
                @on-file-load="onSelectSubtitlesEn"
                v-slot="{fileDragOver}"
              )
                v-btn(outlined :color="fileDragOver ? 'primary' : ''")
                  v-icon.mr-2(v-if="!fileDragOver") mdi-cloud-upload-outline
                  v-icon.mr-2(v-else) mdi-cloud-upload
                  span Upload file
              v-btn(outlined color="primary" v-if="model.video_meta && model.video_meta.subtitles_file_en && !removeSubtitlesEn" @click="onRemoveCurrentSubtitlesEn").ml-4
                v-icon.mr-2 mdi-close
                span Remove current subtitles
          v-col(
            cols="12"
            v-if="model.layout !== LAYOUT_OPTIONS_QUERY.THEORIE"
          )
            h4.mb-3 Thumbnail
              span.hint(v-if="model.layout === LAYOUT_OPTIONS_QUERY.DRAG_AND_DROP && !model.thumbnail")  (Recommend to upload image with min width 900px)
            span.hint(v-if="model.layout === LAYOUT_OPTIONS_QUERY.DRAG_AND_DROP && model.thumbnail") Click on the image to add pointer
            div.image-wrapper.mb-3
              video(
                v-if="model.isVideoThumbnail"
                :src="model.thumbnail"
                controls="controls"
              ).thumbnail
              img(
                v-else-if="model.thumbnail"
                :src="model.thumbnail"
                @click="imageClickHandler"
              ).thumbnail
              template(v-if="model.layout === LAYOUT_OPTIONS_QUERY.DRAG_AND_DROP")
                coordinatesPointer(
                  v-for="pointer in model.pointers"
                  :key="`drag-${pointer.value}`"
                  :pointer="pointer"
                  @delete="model.deletePointer({ value: pointer.value })"
                )
            div.d-flex
              image-upload-crop(
                v-slot="{fileDragOver}"
                @onImageCrop="onImageCropHandler"
                :ratio="[2.222222, 1]"
              )
                v-btn(outlined :color="fileDragOver ? 'primary' : ''")
                  v-icon.mr-2(v-if="!fileDragOver") mdi-cloud-upload-outline
                  v-icon.mr-2(v-else) mdi-cloud-upload
                  span Upload file
              v-btn(outlined color="primary" v-if="model.thumbnail" @click="onRemoveCurrentImage").ml-4
                v-icon.mr-2 mdi-close
                span Remove current file
          v-col.multiple-answers-toggle.pb-0(
            cols="12"
            v-if="[LAYOUT_OPTIONS_QUERY.PICTURE_SELECT, LAYOUT_OPTIONS_QUERY.QUESTIONS].includes(model.layout)"
          )
            v-checkbox.mr-4(
              v-model="model.show_multiple_answers_hint"
              label="Show multiple answers hint"
              :error="formErrors.hasError('show_multiple_answers_hint')"
              @change="formErrors.validField('show_multiple_answers_hint')"
            )
          v-col(
            cols="12"
            v-if="![LAYOUT_OPTIONS_QUERY.THEORIE, LAYOUT_OPTIONS_QUERY.VIDEO, LAYOUT_OPTIONS_QUERY.DRAG_AND_DROP, LAYOUT_OPTIONS_QUERY.NUMERIC, LAYOUT_OPTIONS_QUERY.PICTURE_SELECT].includes(model.layout)"
          )
            h4 Answers
            .d-flex.align-center(v-for="(answer, index) in model.choices" :key="index")
              v-checkbox(
                v-model="answer.isRight"
                :error="formErrors.hasError('answers')"
                @change="formErrors.validField('answers')"
              )
              v-text-field(
                label='Answer title'
                v-model="answer.title"
                :rules="answer.isRight ? [RULES.REQUIRED] : []"
                :error="formErrors.hasError('answers') && answer.isRight"
                single-line
              )
              v-btn(
                @click="model.removeQuestionChoiceByIndex(index)"
                icon
              )
                v-icon mdi-delete
            div.pictures-error(v-if="formErrors.hasError('answers')")
              | {{ formErrors.fields.answers }}
            div.d-flex.align-center.justify-center
              v-btn(color="primary" @click="model.addNewChoice()" small) Add Answer
          v-col(
            cols="6"
            v-if="isNumberLayout"
          )
            h4 Answer
            v-text-field(
              label='Answer'
              v-model="model.numeric_answer"
              :rules="[RULES.REQUIRED]"
              :error="formErrors.hasError('numeric_answer')"
              :error-messages="formErrors.fields.numeric_answer"
              type="number"
              single-line
            )
          v-col(
            cols="6"
            v-if="isNumberLayout"
          )
            h4 Measurement units
            v-select(
              label="Measurement units"
              v-model="model.measurement_units"
              :items="MEASUREMENT_UNITS"
              :rules="[RULES.REQUIRED]"
              :error="formErrors.hasError('measurement_units')"
              :error-messages="formErrors.fields.measurement_units"
              single-line
            )
          v-col(
            cols="12"
            v-if="isPictureLayout"
          )
            h4.mb-5 Answer
            QuestionPictureChoiceItem(
              v-for="(choice, index) in model.choices"
              :key="index"
              :idx="index"
              :choice="choice"
              :form-errors="formErrors"
              @image-saved="formErrors.validField('pictures'); model.setChoicePicture(index, $event)"
              @delete="formErrors.validField('pictures'); model.removeQuestionChoiceByIndex(index)"
            )
            div.pictures-error(v-if="formErrors.hasError('pictures')")
              | {{ formErrors.fields.pictures }}
            div.pictures-error(v-if="formErrors.hasError('answers')")
              | {{ formErrors.fields.answers }}
            div.d-flex.align-center.justify-center
              v-btn(
                color="primary"
                :disabled="model.choices.length >= 4"
                @click="formErrors.validField('pictures'); model.addNewChoice()"
                small
              ) Add Answer
          v-col(
            cols="12"
            v-if="![LAYOUT_OPTIONS_QUERY.THEORIE, LAYOUT_OPTIONS_QUERY.VIDEO].includes(model.layout)"
          )
            h4 Explanation
            v-textarea(
              v-model="model.comment"
              rows="4"
              dense
            ).my-3
    v-divider
    div.d-flex.pa-3(v-if="!isEditMode")
      v-btn(outlined color="primary" @click="hideQuestionItem").ml-auto.mr-3 Cancel
      v-btn(color="primary" depressed @click="addQuestion" :disabled="isSaving")
        v-progress-circular(v-if="isSaving")
        span(v-if="!isSaving") Add
    div.d-flex.pa-3(v-if="isEditMode")
      v-btn(color="primary" outlined @click="duplicateQuestion") Duplicate
      v-btn(color="primary" outlined @click="hideQuestionItem").ml-auto.mr-3 Cancel
      v-btn(color="primary" depressed @click="updateQuestion" :disabled="isSaving")
        v-progress-circular(indeterminate v-if="isSaving" size="16")
        span(v-if="!isSaving") Save
</template>
<script>
import {REQUIRED} from "@/util/validation-rules";
import ErrorMixin from "@/mixins/errors.mixin"
import QuestionItemModel from "./core/models/questionModel"
import QuestionService from "./core/question-service"
import FormErrors from "@/util/form-errors"
import {showSnackbar} from "@/util"
import EventBus from "@/util/event-bus";
import {LIST_EVENTS} from "@/const/list-events.enum";
import {SETTINGS_ENUM} from "@/const/settings.enum";

import {
  LAYOUT_OPTIONS,
  TYPE_OPTIONS,
  LAYOUT_OPTIONS_QUERY,
  PREFILL_GEVAARHERKENNING_VALUES,
  PREFILL_GEVAARHERKENNING_EMPTY_STATEMENTS, MEASUREMENT_UNITS
} from "./core/question-const"
import {ClickElementCoordinatesManager, HtmlElement, HtmlEvent} from '@/util/element-coordinates';
import presignedApi from "@/services/api/exercise/api-media-presigned"
import multipartUploadApi from "@/services/api/exercise/api-multipart-upload"
import QuestionPictureChoiceItem from "@/app/exercise/components/question/QuestionPictureChoiceItem.vue";
import videoStatusPollerMixin from "@/mixins/video-status-poller.mixin";

export default {
  components: {
    QuestionPictureChoiceItem,
    imageUploadCrop: () => import('@/app/exercise/components/ImageUploadCrop.vue'),
    imageCrop: () => import('@/components/global/ImageCrop.vue'),
    coordinatesPointer: () => import('./CoordinatesPointer.vue'),
    SubtitlesUploader: () => import("@/app/exercise/components/question/SubtitlesUploader"),
    VideoUploader: () => import("@/app/exercise/components/question/VideoUploader"),
    VideoEncodingStatus: () => import("@/app/exercise/components/question/VideoEncodingStatus"),
    NuPlayer: () => process.browser ? import('vue-nu-video-player').then(module => module.NuPlayer) : null,
  },
  mixins: [ErrorMixin, videoStatusPollerMixin],
  props: {
    title: String,
    editMode: {
      default: false,
      type: Boolean,
    },
    itemModel: {
      default: null,
      type: Object,
    },
    isVisible: Boolean,
    layout: [String, Array],
  },
  computed: {
    settings() {
      return this.$store.state.settings;
    },
    _title() {
      return this.isEditMode ? 'Edit' : this.title;
    },
    isEditMode() {
      return this.editMode;
    },
    isTheorieLayout() {
      return this.model.layout === LAYOUT_OPTIONS_QUERY.THEORIE;
    },
    isVideoLayout() {
      return this.model.layout === LAYOUT_OPTIONS_QUERY.VIDEO;
    },
    isQuestionLayout() {
      return this.model.layout === LAYOUT_OPTIONS_QUERY.GEVAARHERKENNING || this.model.layout === LAYOUT_OPTIONS_QUERY.QUESTIONS;
    },
    isNumberLayout() {
      return this.model.layout === LAYOUT_OPTIONS_QUERY.NUMERIC;
    },
    isPictureLayout() {
      return this.model.layout === LAYOUT_OPTIONS_QUERY.PICTURE_SELECT;
    },
    video() {
      if (!this.model.video) return null;
      return {
        iFullHd: this.model.video?.ios_full_hd,
        iosHd: this.model.video?.ios_hd,
        iosSd: this.model.video?.ios_sd,
        universalFullHd: this.model.video?.universal_full_hd,
        universalHd: this.model.video?.universal_hd,
        universalSd: this.model.video?.universal_sd,
        status: this.model.video?.status,
        encoding_job: this.model.video?.encoding_job,
      }
    },
    subtitlesLinkNl() {
      if (this.model && this.model.video_meta) {
        return this.model.video_meta.subtitles_file_nl;
      }
      return null;
    },
    subtitlesLinkEn() {
      if (this.model && this.model.video_meta) {
        return this.model.video_meta.subtitles_file_en;
      }
      return null;
    },
  },
  data: () => ({
    MEASUREMENT_UNITS,
    showModal: true,
    svc: null,
    formErrors: null,
    model: new QuestionItemModel(),
    LAYOUT_OPTIONS,
    TYPE_OPTIONS,
    LAYOUT_OPTIONS_QUERY,
    PREFILL_GEVAARHERKENNING_VALUES,
    PREFILL_GEVAARHERKENNING_EMPTY_STATEMENTS,
    RULES: {
      REQUIRED,
    },
    editorPlaceholder: 'Insert text here...',
    cropImage: null,
    cropModalVisible: false,
    cropperOptions: {
      fixed: false,
      fixedBox: false,
      canScale: true,
      full: true,
      centerBox: true
    },
    subtitlesFileNl: null,
    subtitlesFileEn: null,
    videoFile: null,
    removeSubtitlesNl: false,
    removeSubtitlesEn: false,
    removeVideo: false,
    isSaving: false,
    videoProcessing: false,
    fileSavingPercentage: 0,
  }),
  mounted() {
  },
  watch: {
    async itemModel() {
      this.setModelFromProp();
      await this.$nextTick();
      this.resetPolling();
    },
    isVisible(isVisible) {
      if (isVisible) {
        this.setModelFromProp();
        this.prefillLayout();
      } else {
        this.formReset();
      }
    },
    'model.layout'(newVal, oldVal) {
      if (oldVal === LAYOUT_OPTIONS_QUERY.THEORIE) {
        this.model.content = '';
      }
      if (newVal !== LAYOUT_OPTIONS_QUERY.PICTURE_SELECT) {
        this.model.choices.forEach(choice => {
          delete choice.picture;
        })
      }
      if (oldVal) {
        this.model.show_multiple_answers_hint = false;
      }
    },
    'model.content': {
      handler(newVal, oldVal) {
        if (this.isEditMode && !oldVal) {
          this.loadEditorContent(newVal)
        }
      },
      deep: true
    }
  },
  inject: [
    'toggleQuestionCreate',
  ],
  methods: {
    onImageCropHandler(ev) {
      this.model.thumbnail = ev;
      this.isSaving = false;
      this.fileSavingPercentage = 0;
    },
    imageClickHandler(e) {
      if (this.model.layout !== LAYOUT_OPTIONS_QUERY.DRAG_AND_DROP) return;
      const coordinatesManager = new ClickElementCoordinatesManager({
        element: new HtmlElement(e.target),
        event: new HtmlEvent(e),
      })
      this.model.addNewPointer({
        x: coordinatesManager.coordinatesOnElementInPercent.x,
        y: coordinatesManager.coordinatesOnElementInPercent.y,
        value: this.model.newPointerValue
      })
    },
    loadEditorContent(content) {
      //ToDo: remove setTimeout
      setTimeout(() => {
        this.$refs.editor && this.$refs.editor.run('code', content)
      }, 0)
    },
    /* Crop image */
    editorImageUpload(files) {
      const file = files[0]
      const isImage = /^image\//.test(file.type);
      const isMp4 = /^video\/mp4/.test(file.type);
      const isGif = /\/gif$/.test(file.type);
      if (isImage || isMp4) {
        if (isGif || isMp4) { // if image type is gif or video
          this.saveToServer(file);
        } else {
          this.cropImage = file;
          this.cropModalVisible = true;
        }
      } else {
        this.$notify({text: 'You could only upload images or mp4 videos.', type: 'error'})
      }
    },
    async saveToServer(file) {
      const isMp4 = /^video\/mp4/.test(file.type);
      this.videoProcessing = true;
      try {
        const presignedData = await presignedApi.createPresignedUpload(file.name);   
        await presignedApi.uploadToS3(presignedData.upload_params, file);               
        this.insertToEditor(presignedData.file.file, isMp4);
        this.cropModalVisible = false;
      } catch (error) {
        console.error('Error uploading image/video:', error);
        this.processError(error)
      } finally {
        this.videoProcessing = false;
      }
    },
    insertToEditor(url, video = false) {
      const editor = this.$refs.editor
      if (video) {
        const $node = document.createElement('video');
        $node.setAttribute('controls', 'controls');
        $node.setAttribute('src', url + '#t=0.1');
        $node.setAttribute('style', 'max-width: 100%;');
        editor.run('insertNode', $node);
      } else {
        editor.run('insertImage', url);
      }
    },
    /* end Crop image */
    editorChange(content) {
      this.model.content = content;
      this.isSaving = false;
      this.fileSavingPercentage = 0;
    },
    prefillLayout() {
      if (this.layout) {
        this.model.layout = this.layout;
      }
    },
    setOrder(status, order) {
      return status ? order : '';
    },
    onLayoutChange(layout) {
      if (layout === LAYOUT_OPTIONS_QUERY.GEVAARHERKENNING) {
        this.prefillFields()
      }
      this.model.show_multiple_answers_hint = false;
    },
    prefillFields() {
      Object.keys(PREFILL_GEVAARHERKENNING_VALUES).map(prop => {
        const prefillVal = PREFILL_GEVAARHERKENNING_VALUES[prop];
        if (this.countAsEmpty(this.model[prop])) {
          this.model[prop] = JSON.parse(JSON.stringify(prefillVal));
        }
      });
    },
    countAsEmpty(val) {
      const stringVal = JSON.stringify(val);
      return PREFILL_GEVAARHERKENNING_EMPTY_STATEMENTS.includes(stringVal);
    },
    init() {
      this.svc = new QuestionService()
      this.formErrors = new FormErrors()
      this.setModelFromProp();
      EventBus.$on(LIST_EVENTS.FILE_UPLOAD_PROGRESS, (percent) => {
        this.fileSavingPercentage = percent;
        this.isSaving = true;
      });
    },
    hideQuestionItem() {
      this.toggleQuestionCreate(false);
      this.videoFile = null;
      this.subtitlesFileNl = null;
      this.subtitlesFileEn = null;
      this.stopPolling();
      this.$emit('onItemClose');
    },
    formReset() {
      this.model = new QuestionItemModel();
      this.formErrors = new FormErrors();
      this.$refs.form.reset();
    },
    setModelFromProp() {
      Object.assign(this.model, this.itemModel);
    },
    async addQuestion() {
      try {
        if (!this.$refs.form.validate() || !this.model.validate(this.formErrors)) {
          showSnackbar('Invalid fields', {color: 'error'})
          return false;
        }
        this.isSaving = true;
        let res = await this.svc.create(this.model)
        await this.uploadSubtitlesNl(res.id);
        await this.uploadSubtitlesEn(res.id);
        this.$emit('questionCreated');
        this.hideQuestionItem();
      } catch (error) {
        console.log(error);
        this.processErrorWithForm(error, {
          redirectName: this.$ROUTER_NAMES.LOGIN_EXERCISE,
          formErrors: this.formErrors,
          apiAttributes: this.model._apiAttributes
        });
      } finally {
        this.isSaving = false;
        this.fileSavingPercentage = 0;
      }
    },
    async updateQuestion() {
      try {
        if (!this.$refs.form.validate() || !this.model.validate(this.formErrors)) {
          showSnackbar('Invalid fields', {color: 'error'})
          return false;
        }
        this.isSaving = true;
        let res = await this.svc.put(this.model.id, this.model);
        await this.uploadSubtitlesNl(this.model.id);
        await this.uploadSubtitlesEn(this.model.id);
        this.$emit('questionUpdated', {...res, page: this.itemModel.page});
        this.hideQuestionItem();
      } catch (error) {
        console.log(error);
        this.processErrorWithForm(error, {
          redirectName: this.$ROUTER_NAMES.LOGIN_EXERCISE,
          formErrors: this.formErrors,
          apiAttributes: this.model._apiAttributes
        });
      } finally {
        this.isSaving = false;
        this.fileSavingPercentage = 0;
      }
    },
    async duplicateQuestion() {
      try {
        this.itemModel.getApiData = this.model.getApiData;
        this.itemModel._getPointers = this.model._getPointers;
        this.itemModel._getChoices = this.model._getClearChoices;
        await this.svc.create(this.itemModel);
        this.$emit('questionCreated');
        showSnackbar('Item Duplicated');
      } catch (error) {
        this.processErrorWithForm(error, {
          redirectName: this.$ROUTER_NAMES.LOGIN_EXERCISE,
          formErrors: this.formErrors,
          apiAttributes: this.model._apiAttributes
        });
      }
    },
    onRemoveCurrentImage() {
      this.clearCurrentThumbnail();
      this.model.deleteAllPointers();
    },
    onSelectSubtitlesNl(file) {
      this.subtitlesFileNl = file;
      this.removeSubtitlesNl = false;
    },
    onSelectSubtitlesEn(file) {
      this.subtitlesFileEn = file;
      this.removeSubtitlesEn = false;
    },
    onSelectVideo(file) {
      this.videoFile = file;
      this.removeVideo = false;
      this.uploadVideo();
    },
    resetVideoUpload() {
      this.videoFile = null;
      this.model.video_file = null;
    },
    onRemoveCurrentSubtitlesNl() {
      this.subtitlesFileNl = null;
      this.removeSubtitlesNl = true;
    },
    onRemoveCurrentSubtitlesEn() {
      this.subtitlesFileEn = null;
      this.removeSubtitlesEn = true;
    },
    clearCurrentThumbnail() {
      this.model.thumbnail = null;
    },
    async uploadSubtitlesNl(ID) {
      if (this.subtitlesFileNl) {
        try {
          const formData = new FormData();
          formData.append('file', this.subtitlesFileNl);
          await this.svc.uploadSubtitlesNl(ID, formData);
          this.subtitlesFileNl = null;
        } catch (error) {
          this.processError(error)
        }
      } else if (this.removeSubtitlesNl && this.model.video_meta && this.model.video_meta.subtitles_file_nl) {
        await this.svc.uploadSubtitlesNl(ID);
        this.removeSubtitlesNl = false;
      }
    },
    async uploadSubtitlesEn(ID) {
      if (this.subtitlesFileEn) {
        try {
          const formData = new FormData();
          formData.append('file', this.subtitlesFileEn);
          await this.svc.uploadSubtitlesEn(ID, formData);
          this.subtitlesFileEn = null;
        } catch (error) {
          this.processError(error)
        }
      } else if (this.removeSubtitlesEn && this.model.video_meta && this.model.video_meta.subtitles_file_en) {
        await this.svc.uploadSubtitlesEn(ID);
        this.removeSubtitlesEn = false;
      }
    },
    async uploadVideo() {
      if (this.videoFile) {
        try {
          if (multipartUploadApi.isLargeFile(this.videoFile)) {
            const result = await multipartUploadApi.uploadLargeFile(this.videoFile)
            this.model.video_file = result.file
          } else {
            const presignedData = await presignedApi.createPresignedUpload(this.videoFile.name);
            await presignedApi.uploadToS3(presignedData.upload_params, this.videoFile);
            this.model.video_file = presignedData.file;
          }
        } catch (error) {
          showSnackbar('Error on video uploading');
          this.processError(error);
        } finally {
          this.isSaving = false;
        }
      } else if (this.removeVideo && this.model.video_meta) {
        this.model.video_file = null;
        this.removeVideo = false;
      }
    }
  },
}
</script>

<style lang="scss">
/* *** common styles for Summernote *** */
.note-editor {
  .note-group-image-url {
    display: none !important;
  }

  li {
    &[aria-label="blockquote"] {
      display: none !important;
    }

    &[aria-label="pre"] {
      display: none !important;
    }
  }
}
</style>

<style src="summernote/dist/summernote.css"></style>

<style lang="scss" scoped>
@import "@/assets/scss/summernote-content.scss";
.thumbnail {
  max-width: 100%;
}

.image-wrapper {
  position: relative;
}

.main-wrapper ::v-deep {
  @import "@/assets/css/bootstrap";
}

.main-wrapper .body {
  overflow: auto;
  max-height: calc(100vh - 320px);
}

.hint {
  color: $primar-color-light;
  font-weight: 700;
}

::v-deep {
  .v-snack {
    z-index: 9999;
  }
}


.license-types {
  margin-bottom: 8px;

  &__label {
    color: $label-color;
    font-weight: 800;
    font-size: 12px;
    margin-bottom: 2px;
  }

  &__row {
    display: flex;
    align-items: center;

    &::v-deep {
      label {
        font-weight: 400;
        margin-bottom: 0;
      }

      .v-input {
        margin-top: 0;
      }

      .v-messages {
        display: none;
      }
    }
  }

  &__error {
    color: $error-color;
    font-size: 12px;
    margin-bottom: 24px;
    margin-top: -4px;
  }
}

.pictures-error {
  color: $error-color;
  font-size: 12px;
  margin: 8px 0;
}
</style>
